import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IconMenu } from "./BoardToolIcons";

type Props = {
  onExportViewport?: () => void;
  onExportSelection?: () => void;
  onExportLessonPdf?: () => void;
  exportBusy?: boolean;
  hasSelection?: boolean;
  /** На узком экране: копирование гостевой ссылки */
  onCopyShareLink?: () => void;
  shareLinkBusy?: boolean;
  shareLinkLabel?: string;
  showShareLinkInMenu?: boolean;
  onOpenHelp?: () => void;
  onOpenSettings?: () => void;
  /** Именованные снимки состояния доски */
  showCheckpoints?: boolean;
  onOpenCheckpoints?: () => void;
  onOpenBugReport?: () => void;
  /** Куда ведёт «На главную» (школа → /school, иначе /) */
  homeHref?: string;
};

export function BoardPageMenu({
  onExportViewport,
  onExportSelection,
  onExportLessonPdf,
  exportBusy = false,
  hasSelection = false,
  onCopyShareLink,
  shareLinkBusy = false,
  shareLinkLabel = "Ссылка",
  showShareLinkInMenu = false,
  onOpenHelp,
  onOpenSettings,
  showCheckpoints = false,
  onOpenCheckpoints,
  onOpenBugReport,
  homeHref = "/",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: PointerEvent) {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onDoc, true);
    return () => document.removeEventListener("pointerdown", onDoc, true);
  }, [open]);

  return (
    <div className="wb-board-menu" ref={rootRef}>
      <button
        type="button"
        className="wb-btn wb-btn--ghost"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <IconMenu className="wb-board-menu__icon" />
        Меню
      </button>
      {open ? (
        <div className="wb-board-menu__popover" role="menu">
          <div className="wb-board-menu__hint" role="note" aria-label="Горячие клавиши">
            Клавиши инструментов — в «Настройках»
          </div>
          {onOpenSettings ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenSettings();
              }}
            >
              Настройки…
            </button>
          ) : null}
          {showShareLinkInMenu && onCopyShareLink ? (
            <button
              type="button"
              className="wb-board-menu__item wb-board-menu__item--narrow-only"
              role="menuitem"
              disabled={shareLinkBusy}
              onClick={() => {
                setOpen(false);
                onCopyShareLink();
              }}
            >
              {shareLinkLabel === "Ссылка" || shareLinkLabel === "…"
                ? "Скопировать ссылку"
                : shareLinkLabel}
            </button>
          ) : null}
          {onOpenHelp ? (
            <button
              type="button"
              className="wb-board-menu__item wb-board-menu__item--narrow-only"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenHelp();
              }}
            >
              Справка
            </button>
          ) : null}
          {showCheckpoints && onOpenCheckpoints ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenCheckpoints();
              }}
            >
              Снимки доски…
            </button>
          ) : null}
          {onExportViewport ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              disabled={exportBusy}
              onClick={() => {
                setOpen(false);
                onExportViewport();
              }}
            >
              Экспорт PNG (экран)
            </button>
          ) : null}
          {onExportSelection && hasSelection ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              disabled={exportBusy}
              onClick={() => {
                setOpen(false);
                onExportSelection();
              }}
            >
              Экспорт PNG (выделение)
            </button>
          ) : null}
          {onExportLessonPdf ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              disabled={exportBusy}
              onClick={() => {
                setOpen(false);
                onExportLessonPdf();
              }}
            >
              Сохранить урок (PDF)
            </button>
          ) : null}
          <Link className="wb-board-menu__item" role="menuitem" to="/cabinet" onClick={() => setOpen(false)}>
            Мои доски
          </Link>
          <Link className="wb-board-menu__item" role="menuitem" to={homeHref} onClick={() => setOpen(false)}>
            На главную
          </Link>
          {onOpenBugReport ? (
            <button
              type="button"
              className="wb-board-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenBugReport();
              }}
            >
              Сообщить о проблеме или предложить идею…
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
